import {Component, DestroyRef, OnInit} from '@angular/core';
import {PlanModelService} from '../../common/service/plan-model.service';
import {PermissionsService} from '../../common/service/permissions.service';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import { NgClass } from '@angular/common';
import {NlrFormFieldHasErrorPipe} from '../../common/pipe/nlr-form-field-has-error.pipe';
import {ATTACHMENT_CSD, LAND_CSV, PDF_CREATION_METHOD} from '../../common/constants';
import {ProductCodeLookup} from '../../common/models/product-code.model';
import {LookupService} from '../../common/service/lookup.service';
import {AttachmentValidationService} from '../../common/service/attachment-validation.service';
import {FeeService} from '../../common/service/fee.service';
import {debounceTime, distinctUntilChanged, map, Subject} from 'rxjs';
import {ActivatedRoute} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {equalsIgnoreAttrsValues} from '../../common/service/data-util';

export interface FeeSummary {
    itemCode?: string;
    description?: string;
    quantity?: number;
    baseAmount?: string;
    total?: string;
}

@Component({
    selector: 'fee-summary',
    imports: [FormsModule, NlrFormFieldHasErrorPipe, NgClass],
    templateUrl: './fee-summary.component.html',
    styleUrl: './fee-summary.component.scss',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class FeeSummaryComponent implements OnInit {
    numberOfInsetLotsSubject = new Subject<number>();

    title = 'Fee summary';

    insetLotsValues = [
        {value: true, displayValue: 'Yes', active: true},
        {value: false, displayValue: 'No', active: true}
    ];

    feeSummaries: FeeSummary[] = [];

    lastFeeSummaries: FeeSummary[] = [];

    private permissions = ['EDIT_LODGE'];

    private productCodeLookups: ProductCodeLookup[] = this.lookupService.getLookups('productCode');

    private routeAction: string = '';

    constructor(
        private destroyRef: DestroyRef,
        private attachmentValidationService: AttachmentValidationService,
        private feeService: FeeService,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private permissionsService: PermissionsService,
        private route: ActivatedRoute
    ) {}

    ngOnInit() {
        this.route.params
            .pipe(
                map(params => (this.routeAction = params['action'])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();

        this.numberOfInsetLotsSubject.pipe(debounceTime(750), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            if (this.isPlanEditable()) {
                this.feeService.calculateChargeablePlanLots();
            }
            this.feeService.populateFees();
        });
    }

    ngDoCheck() {
        const feeSummaries = this.getFeeSummaries();
        if(!equalsIgnoreAttrsValues(feeSummaries, this.feeSummaries, ['id'], [null, undefined])) {
            this.feeSummaries.splice(0, this.feeSummaries.length, ...feeSummaries);
        }
    }

    displayInsetLots() {
        if (!this.hasPermission()) {
            return false;
        }
        const planModel = this.getModel();
        if (
            (planModel.pdfCreationMethod === PDF_CREATION_METHOD.CAD_SOFTWARE || planModel.pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN) &&
            ['FH_CRAM', 'FH_SUBD'].includes(planModel.planPurpose?.id)
        ) {
            return true;
        }
        planModel.numberOfInsetLots = undefined;
        return false;
    }

    onNumberOfInsetLotsInput(event: Event) {
        const target = event.target as HTMLInputElement;
        if (target.value) {
            const value = parseInt(target.value);
            if (value > 99999999) {
                target.value = `${value}`.slice(0, 8);
                this.getModel().numberOfInsetLots = parseInt(target.value);
            }
        }
        this.numberOfInsetLotsSubject.next(this.getModel().numberOfInsetLots);
    }

    insetLotsChanged() {
        this.getModel().numberOfInsetLots = undefined;
        if (this.isPlanEditable()) {
            this.feeService.calculateChargeablePlanLots();
        }
        this.feeService.populateFees();
    }

    showAdditionalSheetFeeStatement() {
        const {crownStateLand, planPurpose} = this.getModel();

        return (
            planPurpose?.id === 'CR_INTE' ||
            (['CR_RDEF', 'CR_STAT', 'CR_TAKE'].includes(planPurpose?.id) && crownStateLand && !crownStateLand.dolInstructedPlan)
        );
    }

    showAdditionalParcelFeeStatement() {
        const {crownStateLand, lodgementType, tenureType, planLots, planPurpose, planType, chargeablePlanLots} = this.getModel();

        if (
            !tenureType ||
            !planType ||
            !planPurpose ||
            !lodgementType ||
            lodgementType.value !== 'Replacement' ||
            planLots <= this.feeService.previousLotCount ||
            chargeablePlanLots <= this.feeService.previousLotCount
        ) {
            return false;
        }

        const lotFeeProductCodes = ['LODGE-PLAN-LOTFEE', 'LODGE-STRATA-LOTFEE'];

        return this.productCodeLookups.some(
            l =>
                l.lodgementType === 'New' &&
                l.tenureType === tenureType.name &&
                l.planType === planType.name &&
                l.planPurpose === planPurpose.name &&
                lotFeeProductCodes.includes(crownStateLand && crownStateLand.dolInstructedPlan ? l.dolProductCode : l.productCode)
        );
    }

    getZeroChargeableLotsMessage() {
        const model = this.getModel();

        if (
            !model.planPurpose ||
            model.planPurpose.maxNumberLotsCreated === 0 ||
            !this.getFees().some(fee => ['LODGE-PLAN-LOTFEE', 'LODGE-STRATA-LOTFEE'].includes(fee.itemCode))
        ) {
            return '';
        }

        if (model.pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN) {
            return model.chargeablePlanLots === 0 ? 'Parcel fees could not be calculated. No lots have been created for the Digital Plan.' : '';
        }

        if (model.pdfCreationMethod === 'SURVEYOR_PORTAL') {
            if (model.chargeablePlanLots === 0) {
                return 'Parcel fees could not be calculated. No lots have been created on the Design Page.';
            }
        } else if (model.attachments) {
            const attachment = model.attachments.find(a => [ATTACHMENT_CSD, LAND_CSV].includes(a.attachmentType) && a.optionality === 'Mandatory');

            const isAttachmentOptional =
                model.attachments.some(a => a.attachmentType === 'CADASTRAL_SURV' && a.optionality === 'Optional') &&
                !model.attachments.some(a => a.attachmentType === 'LAND_CSV');

            if (attachment && !attachment.fileName) {
                if (attachment.attachmentType === ATTACHMENT_CSD) {
                    return 'Parcel fees could not be calculated. The CSD has not been uploaded.';
                } else {
                    return 'Parcel fees could not be calculated. The Lot Record CSV has not been uploaded.';
                }
            } else if (attachment && attachment.fileName && this.attachmentValidationService.hasValidationInfo(attachment.attachmentType)) {
                const validationErrors = this.attachmentValidationService.getErrors(attachment.attachmentType);
                if (
                    validationErrors.some(
                        v =>
                            v.includes('the document must be in version 2.0 format') ||
                            v.includes('Invalid Lot Record CSV file, parsing error') ||
                            v.includes('lot numbers must be unique')
                    )
                ) {
                    if (validationErrors.some(v => v.includes('the document must be in version 2.0 format'))) {
                        return 'Parcel fees could not be calculated. The attached CSD must be Version 2.0.';
                    } else if (
                        validationErrors.some(
                            v => v.includes('Invalid Lot Record CSV file, parsing error') || v.includes('lot numbers must be unique')
                        )
                    ) {
                        return 'Parcel fees could not be calculated. The attached Lot Record CSV failed validation.';
                    }
                }
            } else if (isAttachmentOptional) {
                if (!model.formerTenures[0]) {
                    return 'Parcel fees could not be calculated. The former tenure has not been entered.';
                }
            }
        }

        return '';
    }

    getFeeSummaries() {
        return this.getFees().map(v => ({
            itemCode: v.itemCode,
            description: v.description || ' ',
            quantity: v.quantity,
            baseAmount: v.baseAmount.toFixed(2),
            total: v.total.toFixed(2)
        }));
    }

    getTotalDue() {
        return this.getFees()
            .reduce((acc: number, v) => acc + v.total, 0)
            .toFixed(2);
    }

    getModel() {
        return this.planModelService.getModel();
    }

    private getFees() {
        return this.getModel().fees || [];
    }

    hasPermission() {
        return this.permissionsService.hasPermission(this.permissions);
    }

    isDisabled() {
        return this.planModelService.readonly || !this.permissionsService.hasEditPermission(this.permissions);
    }

    private isPlanEditable() {
        return ['new', 'edit', 'amend', 'replace'].includes(this.routeAction);
    }
}
